import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { Disclosure } from "@/components/design/Disclosure";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { formatDate } from "@/lib/format-date";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { SaveMatchModal } from "./MatchModals";
import { parseDesign } from "./design-codec";
import { parseTeamResult } from "./match-result-codec";
import type { MatchData } from "./match-design-types";
import type { MatchResultDetailNode, MatchResultListItem, UseMatchEditorResult } from "./use-match-editor";

function teamSizeFromSnapshot(design: MatchResultListItem["design"]): number | null {
  try {
    return parseDesign(design).teamSize;
  } catch {
    return null;
  }
}
import {
  AgentsPanel,
  CandidateRankingPanel,
  DataQualityHintsPanel,
  DemographicsRow,
  FactsAndSkillsBody,
  InfeasibleBanner,
  KpiStrip,
  PersonAvatarsRow,
  PowerClustersPanel,
  SolverErrorBanner,
  TeamPowersBody,
  TopPowersPanel,
  useTeamResultView,
  type ResultTeamLike,
} from "./ResultPanel";
import { PreSolveState } from "./PreSolveState";
import type { ResultPerson } from "./match-result-derive";

function normaliseTeams(
  teams:
    | NonNullable<UseMatchEditorResult["solverResult"]>["teams"]
    | MatchResultDetailNode["teams"]
    | null
    | undefined,
): ResultTeamLike[] {
  return (teams ?? []).map((t) => ({
    teamId: t.teamId,
    result: t.result ?? null,
    persons: (t.persons ?? [])
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => {
        const given = Array.isArray(p.name?.given) ? p.name?.given : [p.name?.given];
        const fullName =
          [...(given ?? []), p.name?.family].filter(Boolean).join(" ") || p.formattedName || "?";
        const av = (p as { avatar?: { content?: string | null; mimeType?: string | null } | null }).avatar;
        return {
          id: p.id,
          name: fullName,
          profession: p.profession ?? null,
          avatarContent: av?.content ?? null,
          avatarMime: av?.mimeType ?? null,
        } satisfies ResultPerson;
      }),
  }));
}

export function ResultStep({
  data,
  editor,
  isReview,
}: {
  data: MatchData;
  editor: UseMatchEditorResult;
  isReview: boolean;
}) {
  const intl = useIntl();
  const [saveOpen, setSaveOpen] = useState(false);
  const [openedResult, setOpenedResult] = useState<MatchResultDetailNode | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);

  // Which saved result is open is mirrored to ?result=<id> in the URL so that
  // browser back to /match/<id>?step=result&result=<rid> restores the opened
  // detail view. Replace semantics — opening/closing a result inside the list
  // shouldn't bloat history; the page-level back is reserved for leaving the
  // match entirely.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlResultId = searchParams.get("result");
  const openResult = editor.openResult;
  const writeResultParam = useCallback(
    (id: string | null) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (id) params.set("result", id);
          else params.delete("result");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  // Cold restore: URL says a saved result should be open, but local state is
  // empty (fresh mount after browser back). Fetch the detail; on failure
  // (deleted, no permission) strip the stale param so the user isn't stuck.
  // The openedResult.id check is read via a ref so closing a saved result
  // (which clears state and URL together) doesn't transiently re-trigger the
  // effect and re-fetch before setSearchParams has propagated.
  const fetchInFlight = useRef<string | null>(null);
  const openedResultIdRef = useRef<string | null>(null);
  openedResultIdRef.current = openedResult?.id ?? null;
  useEffect(() => {
    if (!urlResultId) return;
    if (openedResultIdRef.current === urlResultId) return;
    if (fetchInFlight.current === urlResultId) return;
    fetchInFlight.current = urlResultId;
    void (async () => {
      const detail = await openResult(urlResultId);
      if (fetchInFlight.current !== urlResultId) return;
      fetchInFlight.current = null;
      if (detail) {
        setOpenedResult(detail);
      } else {
        writeResultParam(null);
        setOpenError(
          intl.formatMessage(
            {
              id: "match.result.loadFailed",
              defaultMessage:
                "Ergebnis konnte nicht geladen werden (id {id}). Browser-Konsole prüfen.",
            },
            { id: urlResultId },
          ),
        );
      }
    })();
  }, [urlResultId, openResult, writeResultParam, intl]);
  const canEditContent =
    editor.myRole === "owner" || editor.myRole === "admin" || editor.myRole === "editor";

  const activeTeams: ResultTeamLike[] = openedResult
    ? normaliseTeams(openedResult.teams)
    : editor.solverResult
      ? normaliseTeams(editor.solverResult.teams)
      : [];
  // Anforderungspruefung des ANGEZEIGTEN Ergebnisses: gespeicherte Results tragen ihren
  // Design-Snapshot, der Live-Lauf nutzt den aktuellen Editor-Stand. Treibt die amber
  // Kulanz-Markierung im Ranking.
  const requirementLeniency = openedResult
    ? (parseDesign(openedResult.design).requirementLeniency ?? "LENIENT")
    : data.requirementLeniency;
  const hasResult =
    activeTeams.length > 0 && activeTeams.some((t) => t.persons.length > 0);
  const team = hasResult ? activeTeams[0] : null;
  const isInfeasible =
    !openedResult &&
    editor.solverResult !== null &&
    !editor.solverLoading &&
    !editor.solverError &&
    !hasResult;
  // Genesis carries the infeasibility class + relax-MUST culprits in the (empty)
  // team's opaque result blob; null when the backend returned no team node at all
  // (→ generic banner).
  const infeasibility = isInfeasible
    ? parseTeamResult(activeTeams[0]?.result ?? null).infeasibility
    : null;
  useAssistantContext(
    team || isInfeasible
      ? [
          {
            description: "the match-result view currently open",
            value: [
              openedResult
                ? `saved result "${openedResult.name || "unnamed"}"`
                : "newly calculated, not-yet-saved result",
              `match mode ${data.matchMode ?? "OPTIMIZE"}`,
              team ? `${team.persons.length} candidates in the result` : "no feasible result",
              openedResult?.notes ? `result notes: ${openedResult.notes}` : null,
              infeasibility?.reason ? `solver reason: ${infeasibility.reason}` : null,
            ]
              .filter(Boolean)
              .join("; ")
              .slice(0, 1_900),
          },
        ]
      : null,
  );

  async function handleOpenResult(id: string) {
    setOpenError(null);
    const detail = await editor.openResult(id);
    if (detail) {
      setOpenedResult(detail);
      writeResultParam(id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setOpenError(
        intl.formatMessage(
          {
            id: "match.result.loadFailed",
            defaultMessage:
              "Ergebnis konnte nicht geladen werden (id {id}). Browser-Konsole prüfen.",
          },
          { id },
        ),
      );
    }
  }
  async function handleSaveResult({ name, notes }: { name: string; notes?: string }) {
    await editor.saveResult({ name, notes });
    setSaveOpen(false);
  }
  function handleBackToPreSolve() {
    const hasUnsavedTransient =
      Boolean(editor.solverResult) && !editor.solverResultSaved && !openedResult && hasResult;
    if (hasUnsavedTransient) {
      setConfirmDiscardOpen(true);
      return;
    }
    backToPreSolve();
  }

  function backToPreSolve() {
    setConfirmDiscardOpen(false);
    setOpenedResult(null);
    writeResultParam(null);
    editor.clearSolverResult();
  }
  return (
    <div className="space-y-6">
      <ConfirmDialog
        open={confirmDiscardOpen}
        title={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.title"
            defaultMessage="Ungespeichertes Ergebnis verwerfen?"
          />
        }
        message={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.message"
            defaultMessage="Das berechnete Ergebnis wurde noch nicht gespeichert und geht beim Zurückgehen verloren."
          />
        }
        confirmLabel={
          <FormattedMessage id="match.result.discardUnsavedConfirm.confirm" defaultMessage="Verwerfen" />
        }
        onConfirm={backToPreSolve}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
      <div>
        {openedResult && (
          <div className="field-label mb-2">
            <FormattedMessage
              id="match.result.savedResultLabel"
              defaultMessage="Gespeichertes Ergebnis · {name}"
              values={{
                name:
                  openedResult.name ??
                  intl.formatMessage({
                    id: "match.result.unnamed",
                    defaultMessage: "ohne Namen",
                  }),
              }}
            />
          </div>
        )}
        {(hasResult || isInfeasible) && (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleBackToPreSolve}
            className="mb-2"
          >
            <FormattedMessage
              id="match.result.backToSelection"
              defaultMessage="← Schließen"
            />
          </Button>
        )}
        {/* Pre-solve needs no subtitle: the PreSolveState panel below is
            self-describing (eyebrow + heading + action). A subtitle only earns
            its place over a result/saved view, which has no such panel. */}
        {(openedResult || isReview) && (
          <p className="max-w-2xl text-sm text-ink/70">
            {openedResult
              ? intl.formatMessage({
                  id: "match.result.savedSubtitle",
                  defaultMessage: "Du siehst ein gespeichertes Ergebnis dieses Matches.",
                })
              : intl.formatMessage({
                  id: "match.result.reviewSubtitle",
                  defaultMessage:
                    "Optimiert auf dein Anforderungsprofil – transparent, erklärbar, exportierbar.",
                })}
          </p>
        )}
        {editor.solverLoading && (
          <p className="mt-2 text-[11px] text-ink/55">
            <FormattedMessage id="match.result.solverRunning" defaultMessage="Solver läuft …" />
          </p>
        )}
      </div>

      {editor.solverError && !editor.solverLoading && (
        <SolverErrorBanner error={editor.solverError} onRetry={() => void editor.runSolver()} />
      )}

      {isInfeasible && (
        <InfeasibleBanner
          infeasibility={infeasibility}
          onManagePeople={() => {
            editor.clearSolverResult();
            editor.setStepId("people");
          }}
          onAdjustRequirements={() => {
            editor.clearSolverResult();
            editor.setStepId("challenge");
          }}
        />
      )}

      {openError && (
        <div className="box bg-rose-50 p-4 text-[12px] text-rose-700">{openError}</div>
      )}

      {team ? (
        <PostSolveDashboard team={team} data={data} requirementLeniency={requirementLeniency} />
      ) : (
        <>
          {!isInfeasible && <PreSolveState editor={editor} />}
          <ResultsHistory
            editor={editor}
            onOpen={handleOpenResult}
            openedResultId={openedResult?.id ?? null}
          />
        </>
      )}

      {canEditContent && editor.solverResult && !editor.solverResultSaved && !openedResult && !isInfeasible && (
        // sticky (not fixed) so the button respects the main-content
        // container's right edge — fixed would overlap the agent sidebar
        // when it's open (ADR-0019). pointer-events-none on the wrapper lets
        // clicks fall through to underlying content; the button itself
        // re-enables pointer-events for its own clickable area.
        <div className="pointer-events-none sticky bottom-4 z-10 flex justify-end sm:bottom-8">
          <Button
            variant="primary"
            onClick={() => setSaveOpen(true)}
            className="pointer-events-auto px-5 py-2.5 text-[11px] shadow-xl"
          >
            <FormattedMessage id="match.result.saveResult" defaultMessage="Ergebnis speichern" />
          </Button>
        </div>
      )}

      {saveOpen && (
        <SaveMatchModal
          defaultName={data.matchTitle || data.projectName}
          defaultNotes=""
          onClose={() => setSaveOpen(false)}
          onSave={handleSaveResult}
          previewPersons={team?.persons ?? []}
          previewMeta={{
            teamSize: data.teamSize,
            candidateCount: editor.solverResult?.candidateCount ?? null,
            requirementsCount: data.conditions.length,
            agenticAi: Boolean(data.agenticAi),
            // Immutable mode is the source of truth (ADR-0029); fall back to the assessOnly flag only
            // for legacy matches with no matchMode (pre-#159), which never had a RANK case.
            matchMode: data.matchMode ?? (editor.assessOnly ? "ASSESS" : "OPTIMIZE"),
          }}
        />
      )}
    </div>
  );
}

function PostSolveDashboard({
  team,
  data,
  requirementLeniency,
}: {
  team: ResultTeamLike;
  data: MatchData;
  requirementLeniency: MatchData["requirementLeniency"];
}) {
  const view = useTeamResultView(team);

  // Ranken-Modus: nur das A/B/C-Ranking, keine Team-/Power-Boxen —
  // die sind auf Team-Komposition zugeschnitten und fuers Kandidaten-Ranking Rauschen.
  if (data.matchMode === "RANK") {
    return (
      <div className="space-y-6">
        <CandidateRankingPanel
          view={view}
          persons={team.persons}
          elementWeights={data.teamProfile?.elementWeights}
          requirementLeniency={requirementLeniency}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <DataQualityHintsPanel view={view} />

      <div className="box bg-surface p-6">
        <BoxHeader
          title={
            <FormattedMessage
              id="match.result.teamHeader"
              defaultMessage="Team · {count, plural, one {# Person} other {# Personen}}"
              values={{ count: team.persons.length }}
            />
          }
        />
        <PersonAvatarsRow persons={team.persons} />
        <DemographicsRow view={view} />
      </div>

      <KpiStrip view={view} persons={team.persons} teamSizeTarget={data.teamSize} />

      <div className="grid grid-cols-1 gap-4 @lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <TopPowersPanel view={view} />
        <PowerClustersPanel view={view} />
      </div>

      {data.agenticAi ? <AgentsPanel /> : null}

      <div className="box bg-surface p-5">
        <BoxHeader title={<FormattedMessage id="match.result.detailsHeader" defaultMessage="Details" />} />
        <div className="space-y-2">
          <Disclosure title={<FormattedMessage id="match.result.factsAndSkills" defaultMessage="Facts & Skills" />}>
            <FactsAndSkillsBody view={view} persons={team.persons} />
          </Disclosure>
          <Disclosure title={<FormattedMessage id="match.result.teamPowers" defaultMessage="Team Powers" />}>
            <TeamPowersBody view={view} persons={team.persons} />
          </Disclosure>
        </div>
      </div>
    </div>
  );
}

function ResultsHistory({
  editor,
  onOpen,
  openedResultId,
}: {
  editor: UseMatchEditorResult;
  onOpen: (id: string) => Promise<void>;
  openedResultId: string | null;
}) {
  const intl = useIntl();
  const [pendingDelete, setPendingDelete] = useState<MatchResultListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (editor.resultsLoading && editor.results.length === 0) {
    return (
      <div className="text-[11px] text-ink/45">
        <FormattedMessage id="match.result.historyLoading" defaultMessage="Gespeicherte Ergebnisse laden …" />
      </div>
    );
  }
  if (editor.results.length === 0) return null;

  const canDelete =
    editor.myRole === "owner" || editor.myRole === "admin" || editor.myRole === "editor";

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await editor.deleteResult(pendingDelete.id);
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={<FormattedMessage id="match.result.historyTitle" defaultMessage="Gespeicherte Ergebnisse" />}
        right={<span className="text-[11px] text-ink/45 tabular-nums">{editor.results.length}</span>}
      />
      <ul className="divide-y divide-ink/5">
        {editor.results.map((r) => {
          const isActive = r.id === openedResultId;
          const teamSize = teamSizeFromSnapshot(r.design);
          const meta = [
            r.createdAt ? formatDate(r.createdAt, intl.locale) : null,
            // Teamgröße ist ein Team-Zusammenstellungs-Wert (nur Optimize). Im Assess/Rank-Modus
            // (assessOnly) werden einzelne Kandidaten gegen eine Stelle bewertet, dort ist sie
            // bedeutungslos und wird ausgeblendet.
            !editor.assessOnly && teamSize && teamSize > 0
              ? intl.formatMessage(
                  { id: "match.result.teamSizeMeta", defaultMessage: "Teamgröße {count}" },
                  { count: teamSize },
                )
              : null,
            intl.formatMessage(
              {
                id: "match.result.candidatesMeta",
                defaultMessage: "{count, plural, one {# Kandidat} other {# Kandidaten}}",
              },
              { count: r.candidateCount ?? 0 },
            ),
            r.createdBy?.formattedName ?? null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={r.id} className="flex items-center gap-3 py-2.5">
              <button
                onClick={() => void onOpen(r.id)}
                className="-mx-2 min-w-0 flex-1 cursor-pointer rounded-md px-2 py-1 text-left transition hover:bg-ink/[0.06]"
              >
                <div className="truncate text-[13px] font-medium">
                  {r.name ??
                    intl.formatMessage({
                      id: "match.result.unnamed",
                      defaultMessage: "ohne Namen",
                    })}
                </div>
                <div className="text-[10px] text-ink/55">{meta}</div>
                {r.notes && (
                  <div className="mt-1 truncate text-[11px] italic text-ink/55">{r.notes}</div>
                )}
              </button>
              {isActive && (
                <span className="text-[10px] uppercase tracking-[0.2em] text-accent">
                  <FormattedMessage id="match.result.openFlag" defaultMessage="offen" />
                </span>
              )}
              {canDelete && (
                <Button
                  variant="icon"
                  size="sm"
                  onClick={() => setPendingDelete(r)}
                  title={intl.formatMessage({
                    id: "match.result.deleteTitle",
                    defaultMessage: "Löschen",
                  })}
                  aria-label={intl.formatMessage(
                    {
                      id: "match.result.deleteAriaLabel",
                      defaultMessage: 'Ergebnis „{name}" löschen',
                    },
                    {
                      name:
                        r.name ??
                        intl.formatMessage({
                          id: "match.result.unnamed",
                          defaultMessage: "ohne Namen",
                        }),
                    },
                  )}
                >
                  ✕
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={intl.formatMessage({
          id: "match.result.deleteConfirmTitle",
          defaultMessage: "Ergebnis löschen?",
        })}
        message={
          pendingDelete && (
            <div className="space-y-2">
              <p>
                <FormattedMessage
                  id="match.result.deleteConfirmBody"
                  defaultMessage='Das gespeicherte Ergebnis <b>„{name}"</b> wird endgültig entfernt. Diese Aktion kann nicht rückgängig gemacht werden.'
                  values={{
                    name:
                      pendingDelete.name ??
                      intl.formatMessage({
                        id: "match.result.unnamed",
                        defaultMessage: "ohne Namen",
                      }),
                    b: (chunks) => <span className="font-medium text-ink">{chunks}</span>,
                  }}
                />
              </p>
              <p className="text-[11px] text-ink/55">
                {[
                  pendingDelete.createdAt ? formatDate(pendingDelete.createdAt, intl.locale) : null,
                  (() => {
                    const ts = teamSizeFromSnapshot(pendingDelete.design);
                    return !editor.assessOnly && ts && ts > 0
                      ? intl.formatMessage(
                          {
                            id: "match.result.teamSizeMeta",
                            defaultMessage: "Teamgröße {count}",
                          },
                          { count: ts },
                        )
                      : null;
                  })(),
                  intl.formatMessage(
                    {
                      id: "match.result.candidatesMeta",
                      defaultMessage: "{count, plural, one {# Kandidat} other {# Kandidaten}}",
                    },
                    { count: pendingDelete.candidateCount ?? 0 },
                  ),
                  pendingDelete.createdBy?.formattedName ?? null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            </div>
          )
        }
        confirmLabel={
          deleting
            ? intl.formatMessage({ id: "match.result.deleting", defaultMessage: "Lösche…" })
            : intl.formatMessage({
                id: "match.result.deleteFinal",
                defaultMessage: "Endgültig löschen",
              })
        }
        cancelLabel={intl.formatMessage({ id: "common.cancel", defaultMessage: "Abbrechen" })}
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
}
